import { z } from 'zod';
import type { PlanFeatures } from './limits.js';
import { createServiceFromBlueprintSchema } from './service-blueprints.js';

/**
 * One-click service provisioning — Coolify's service templates plus a
 * staff-only custom docker-compose path (POST /services).
 *
 * Coolify has no API endpoint listing the templates an instance supports;
 * the catalog is assembled panel-side from the version-pinned
 * `templates/service-templates.json` in the Coolify GitHub repository and
 * cached in the panel database (ADR-0008). Entries are filtered by their
 * `minversion` against the connected instance's version.
 */

// ── Template catalog ────────────────────────────────────────────────

export interface ServiceTemplateDto {
  /** Template key, doubles as Coolify's `type` on POST /services. */
  slug: string;
  /** Humanized slug for display, e.g. "calibre-web" → "Calibre Web". */
  name: string;
  slogan: string;
  category: string;
  tags: string[];
  /** Absolute URL to the template's logo (served from the Coolify repo). */
  logoUrl: string | null;
  documentation: string | null;
  /** Default HTTP port the template exposes, as published upstream. */
  port: string | null;
  /** Minimum Coolify version the template needs. */
  minversion: string;
}

export interface ServiceCatalogDto {
  /** Version of the connected Coolify instance the catalog was built for. */
  coolifyVersion: string | null;
  /** Where the templates JSON was fetched from (git tag or main fallback). */
  source: string | null;
  fetchedAt: string | null;
  /** True when serving a cached copy because a refresh failed. */
  stale: boolean;
  /** Template count after minversion filtering, before search filters. */
  total: number;
  categories: Array<{ name: string; count: number }>;
  templates: ServiceTemplateDto[];
}

export const serviceCatalogQuerySchema = z.object({
  search: z.string().trim().max(200).optional(),
  category: z.string().trim().max(100).optional(),
  tag: z.string().trim().max(100).optional(),
});
export type ServiceCatalogQuery = z.infer<typeof serviceCatalogQuerySchema>;

/** "calibre-web" → "Calibre Web", "grafana-with-postgresql" → "Grafana With Postgresql". */
export function templateDisplayName(slug: string): string {
  return slug
    .split(/[-_]/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

/**
 * Tolerant comparison of Coolify version strings ("4.1.2",
 * "4.0.0-beta.228"). Numeric dot-parts compare numerically; a release
 * outranks any prerelease of the same version; prerelease segments compare
 * numerically where possible. Unparseable segments compare as 0 — for a
 * catalog filter, being permissive beats being wrong.
 */
export function compareCoolifyVersions(a: string, b: string): number {
  const parse = (v: string): { main: number[]; pre: Array<number | string> | null } => {
    const [main = '', ...preParts] = v.trim().split('-');
    const pre = preParts.length ? preParts.join('-').split('.') : null;
    return {
      main: main.split('.').map((p) => Number.parseInt(p, 10) || 0),
      pre: pre ? pre.map((p) => (/^\d+$/.test(p) ? Number.parseInt(p, 10) : p)) : null,
    };
  };
  const va = parse(a);
  const vb = parse(b);
  for (let i = 0; i < Math.max(va.main.length, vb.main.length); i += 1) {
    const diff = (va.main[i] ?? 0) - (vb.main[i] ?? 0);
    if (diff !== 0) return Math.sign(diff);
  }
  if (va.pre === null && vb.pre === null) return 0;
  if (va.pre === null) return 1; // release > prerelease
  if (vb.pre === null) return -1;
  for (let i = 0; i < Math.max(va.pre.length, vb.pre.length); i += 1) {
    const pa = va.pre[i];
    const pb = vb.pre[i];
    if (pa === undefined) return -1;
    if (pb === undefined) return 1;
    if (pa === pb) continue;
    if (typeof pa === 'number' && typeof pb === 'number') return Math.sign(pa - pb);
    return String(pa) < String(pb) ? -1 : 1;
  }
  return 0;
}

/** Whether a template's minversion is satisfied by the instance version. */
export function templateSupported(template: { minversion: string }, instanceVersion: string): boolean {
  return compareCoolifyVersions(template.minversion, instanceVersion) <= 0;
}

/** In-memory search over the (already minversion-filtered) catalog. */
export function searchTemplates(
  templates: ServiceTemplateDto[],
  query: ServiceCatalogQuery,
): ServiceTemplateDto[] {
  const search = query.search?.toLowerCase();
  const tag = query.tag?.toLowerCase();
  return templates.filter((t) => {
    if (query.category && t.category !== query.category) return false;
    if (tag && !t.tags.some((x) => x.toLowerCase() === tag)) return false;
    if (
      search &&
      !t.slug.includes(search) &&
      !t.name.toLowerCase().includes(search) &&
      !t.slogan.toLowerCase().includes(search) &&
      !t.tags.some((x) => x.toLowerCase().includes(search))
    ) {
      return false;
    }
    return true;
  });
}

// ── Create / update ─────────────────────────────────────────────────

const commonServiceFields = {
  subscriptionId: z.string().uuid(),
  /** Which Coolify server runs the service stack. */
  serverUuid: z.string().trim().min(1).max(100),
  name: z.string().trim().min(1).max(200),
  description: z.string().max(2000).nullish(),
  /** Start the service right after creation. */
  instantDeploy: z.boolean().default(false),
};

export const createServiceSchema = z.discriminatedUnion('source', [
  createServiceFromBlueprintSchema.extend(commonServiceFields),
  z.object({
    source: z.literal('template'),
    /** Catalog slug — becomes Coolify's `type`. */
    templateSlug: z.string().trim().min(1).max(100),
    ...commonServiceFields,
  }),
  z.object({
    source: z.literal('compose'),
    /** Raw docker-compose YAML; the panel base64-encodes it for Coolify. */
    dockerComposeRaw: z.string().min(1).max(100_000),
    ...commonServiceFields,
  }),
]);
export type CreateServiceInput = z.infer<typeof createServiceSchema>;

export const updateServiceSchema = z.object({
  name: z.string().trim().min(1).max(200).optional(),
  description: z.string().max(2000).nullish(),
});
export type UpdateServiceInput = z.infer<typeof updateServiceSchema>;

export const linkServiceSchema = z.object({
  coolifyUuid: z.string().trim().min(1).max(100),
});
export type LinkServiceInput = z.infer<typeof linkServiceSchema>;

// ── DTOs ────────────────────────────────────────────────────────────

/** One container inside a service stack (Coolify nests apps + databases). */
export interface ServiceComponentDto {
  kind: 'application' | 'database';
  name: string;
  status: string | null;
  fqdn: string | null;
  image: string | null;
}

/** Live service state read from Coolify (defensively parsed). */
export interface CoolifyServiceDto {
  uuid: string;
  name: string;
  description: string | null;
  status: string | null;
  /** The template slug the service was created from, when known. */
  serviceType: string | null;
  components: ServiceComponentDto[];
  createdAt: string | null;
  updatedAt: string | null;
}

export interface ServiceDetailDto {
  resource: {
    id: string;
    subscriptionId: string;
    name: string;
    coolifyUuid: string | null;
    status: string;
    details: Record<string, unknown> | null;
    createdAt: string;
  };
  subscription: {
    id: string;
    name: string;
    clientId: string;
    clientName: string;
    status: string;
  };
  features: PlanFeatures;
  live: CoolifyServiceDto | null;
  liveError: string | null;
  /**
   * Coolify instance URL for "open in Coolify" links — runtime logs for
   * services are not available through the Coolify API, only its UI.
   */
  coolifyBaseUrl: string | null;
}

export const SERVICES_MANAGE_FEATURE = 'services_manage';
/** Provider-curated service templates; advisory blueprints cannot be installed here. */
export const CUSTOMER_SERVICE_BLUEPRINTS = ['umami-analytics', 'ghost-cms', 'directus-cms'] as const;
export const CUSTOMER_SERVICE_NAMES: Record<(typeof CUSTOMER_SERVICE_BLUEPRINTS)[number], string> = {
  'umami-analytics': 'Umami analytics', 'ghost-cms': 'Ghost publishing', 'directus-cms': 'Directus content management',
};
export const customerServicePolicySchema = z.object({
  serverUuid: z.string().trim().max(100),
  blueprintIds: z.array(z.enum(CUSTOMER_SERVICE_BLUEPRINTS)).max(3),
}).strict();
export type CustomerServicePolicyDto = z.infer<typeof customerServicePolicySchema>;
export const createCustomerServiceSchema = createServiceFromBlueprintSchema.extend({
  subscriptionId: commonServiceFields.subscriptionId,
  name: commonServiceFields.name,
  description: commonServiceFields.description,
  instantDeploy: commonServiceFields.instantDeploy,
}).strict();
export interface CustomerServiceOptionsDto {
  configured: boolean;
  blueprints: Array<{ id: string; name: string; summary: string; available: boolean }>;
}
export interface CustomerServiceCreatedDto {
  service: { id: string; name: string; status: string };
  warning: string | null;
}
export interface CustomerServiceDetailDto {
  id: string;
  name: string;
  status: string;
  linked: boolean;
  deploymentsEnabled: boolean;
  liveError: string | null;
}
